import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton,
  Card, CardContent, Grid, Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useOpsAlerts, useOpsHealth, useOpsActivity, useUpdateAlert, useRetryAlert } from '../../hooks/useOps';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  return `vor ${Math.floor(hrs / 24)} Tag(en)`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const OpsTab: React.FC = () => {
  const { data: alerts } = useOpsAlerts();
  const { data: health } = useOpsHealth();
  const { data: activity } = useOpsActivity();
  const updateAlert = useUpdateAlert();
  const retryAlert = useRetryAlert();

  return (
    <Box>
      {/* Health Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Jobs Queue</Typography>
              <Typography variant="h4">
                {(health?.queues.jobs.pending || 0) + (health?.queues.jobs.running || 0)}
              </Typography>
              <Typography variant="caption">
                {health?.queues.jobs.pending || 0} wartend, {health?.queues.jobs.running || 0} laufend
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Optimierung Queue</Typography>
              <Typography variant="h4">
                {(health?.queues.optimization.pending || 0) + (health?.queues.optimization.running || 0)}
              </Typography>
              <Typography variant="caption">
                {health?.queues.optimization.pending || 0} wartend, {health?.queues.optimization.running || 0} laufend
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Watchdog</Typography>
              <Typography variant="h4" color={
                health?.last_watchdog_at &&
                (Date.now() - new Date(health.last_watchdog_at + 'Z').getTime()) < 600000
                  ? 'success.main' : 'error.main'
              }>
                {health?.last_watchdog_at ? relativeTime(health.last_watchdog_at) : 'Nie'}
              </Typography>
              <Typography variant="caption">
                {health?.open_alert_count || 0} offene Alerts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>L5 Crawl Storage</Typography>
              <Typography variant="h4">
                {health?.l5_crawl_storage?.count ?? 0}
              </Typography>
              <Typography variant="caption">
                {formatBytes(health?.l5_crawl_storage?.bytes ?? 0)} (Auto-Purge nach 60 Tagen)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts Table */}
      <Typography variant="h6" sx={{ mb: 1 }}>Aktive Alerts</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Mandant</TableCell>
              <TableCell>Beschreibung</TableCell>
              <TableCell>Seit</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts && alerts.length > 0 ? alerts.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.severity === 'critical'
                    ? <ErrorIcon color="error" fontSize="small" />
                    : <WarningIcon color="warning" fontSize="small" />}
                </TableCell>
                <TableCell>
                  <Chip label={a.alert_type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{a.tenant_name || '—'}</TableCell>
                <TableCell>{a.subject}</TableCell>
                <TableCell>{a.created_at ? relativeTime(a.created_at) : '—'}</TableCell>
                <TableCell align="right">
                  {a.status === 'open' && (
                    <Tooltip title="Bestätigen">
                      <IconButton size="small" onClick={() =>
                        updateAlert.mutate({ id: a.id, status: 'acknowledged' })
                      }>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Erledigt">
                    <IconButton size="small" onClick={() =>
                      updateAlert.mutate({ id: a.id, status: 'resolved' })
                    }>
                      <DoneAllIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Erneut versuchen">
                    <IconButton size="small" onClick={() => retryAlert.mutate(a.id)}>
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>Keine aktiven Alerts</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Activity Log */}
      <Typography variant="h6" sx={{ mb: 1 }}>Letzte Aktivität (24h)</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Zeit</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activity && activity.length > 0 ? activity.slice(0, 50).map((item, i) => (
              <TableRow key={`${item.type}-${item.id}-${i}`}>
                <TableCell>{item.timestamp ? relativeTime(item.timestamp) : '—'}</TableCell>
                <TableCell>
                  <Chip label={item.type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>#{item.id}</TableCell>
                <TableCell>
                  <Chip
                    label={item.status}
                    size="small"
                    color={item.status === 'completed' ? 'success' : 'error'}
                  />
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>Keine Aktivität</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OpsTab;
